import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCartModal } from "../../context/CartModalContext";
import Faq from "../FAQ/FAQ";
import { formatName } from "@/utils/formatName";
import styles from "./ProductDetailCard.module.scss";
import { getSiteConfig } from "@/config/siteConfigs";

const siteConfig = getSiteConfig();
type OrderType = "exchpart" | "sellpart";
type Mode = { id: string; label: string };

interface ProductDetailCardProps {
  id: string;
  catid: string;
  name: string;
  pricevat: number;
  priceexchvat: number;
  images?: string[];
  descr: string;
  table: string;
  onAddToCart: (
    goid: string,
    quantity: number,
    table: string,
    typeorder: OrderType
  ) => void;
}

const ProductDetailCard: React.FC<ProductDetailCardProps> = ({
  id,
  catid,
  name,
  pricevat,
  priceexchvat,
  images = [],
  descr,
  table,
  onAddToCart,
}) => {
  const { t } = useTranslation("common");
  const fallbackImage = "/images/Shop/template.webp";
  const { openCart } = useCartModal();
  const [quantity, setQuantity] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [categoryTree, setCategoryTree] = useState<Record<string, string>>({});
  const [active, setActiveState] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const modes: Mode[] = [
    {
      id: "post",
      label: t("product.orderModes.exchange"),
    },
    {
      id: "onsite",
      label: t("product.orderModes.sale"),
    },
  ];

  const count = modes.length;

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value)) {
      setQuantity(Math.max(1, value));
    }
  };

  useEffect(() => {
    if (priceexchvat && priceexchvat > 0) {
      setActiveState(0);
    } else {
      setActiveState(1);
    }
  }, [priceexchvat]);

  useEffect(() => {
    const fetchCategoryTree = async () => {
      if (!catid) {
        setCategoryTree({});
        return;
      }

      try {
        const response = await fetch("https://crm.hixa.eu/external/goodsapi/", {
          method: "POST",
          headers: {
            Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
          body: new URLSearchParams({
            type: "category",
            catid: catid,
            legid: "1",
            language: siteConfig.language,
            table: table,
          }),
        });

        const data = await response.json();
        if (data.success && data.tree) {
          setCategoryTree(data.tree);
        }
      } catch (error) {
        console.error("Error fetching category tree:", error);
      }
    };

    fetchCategoryTree();
  }, [catid, table]);

  const typeorder = active === 0 ? "exchpart" : "sellpart";

  const handleAdd = () => {
    onAddToCart(id, quantity, table, typeorder);
    openCart();
  };

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const section = table === "service" ? "services" : "products";

  return (
    <>
      <div className={`${styles.productDetailPage} brxeContainer`}>
        <div className="sideP88 titleCont noBorder">
          <span className={styles.navigation}>
            <Link to="/">{t("product.breadcrumbs.home")}</Link>
            <span className={styles.separator}>/</span>
            <Link to="/shop">{t("product.breadcrumbs.shop")}</Link>

            {Object.entries(categoryTree).map(([catId, catName]) => (
              <React.Fragment key={catId}>
                <span className={styles.separator}>/</span>
                <Link to={`/shop/${section}/${catId}/${formatName(catName)}`}>
                  {catName}
                </Link>
              </React.Fragment>
            ))}

            <span className={styles.separator}>/</span>
            <p className={styles.navName}>{name}</p>
          </span>
        </div>
        <div className="sideP64">
          <div className={styles.productDetailBlock}>
            <div className={styles.card}>
              <div className={styles.imageWrapper}>
                <img
                  src={images[currentIndex] || fallbackImage}
                  alt={name}
                  className={styles.mainImage}
                />
                <div className={styles.thumbnails}>
                  {images.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`${name} ${index + 1}`}
                      className={`${styles.thumb} ${
                        currentIndex === index ? styles.active : ""
                      }`}
                      onClick={() => setCurrentIndex(index)}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.descriptionWrapper}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: descr,
                  }}
                />
                <Faq
                  question={name}
                  answer={descr}
                  isOpen={isOpen}
                  onToggle={toggleOpen}
                />
              </div>
            </div>

            <div className={styles.orderBlock}>
              <div className={styles.priceBlock}>
                {active === 0 ? (
                  <span className={styles.price}>
                    {priceexchvat > 0 ? priceexchvat : pricevat} €
                  </span>
                ) : (
                  <span className={styles.price}>{pricevat} €</span>
                )}

                <span className={styles.priceInfo}>
                  {t("product.priceInfo")}
                </span>

                {priceexchvat > 0 && (
                  <div className={styles.segmented}>
                    <div
                      className={styles.segTrack}
                      style={
                        {
                          "--count": count,
                          "--index": active,
                        } as React.CSSProperties
                      }
                      role="tablist"
                      aria-label={t("product.orderModes.sale")}
                    >
                      <div className={styles.segSlider} aria-hidden="true" />

                      {modes.map((m, i) => (
                        <button
                          key={m.id}
                          role="tab"
                          aria-selected={i === active}
                          className={`${styles.segOption} ${
                            i === active ? styles.isActive : ""
                          }`}
                          onClick={() => {
                            setActiveState(i);
                          }}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <p className={styles.priceSubInfo}>
                <svg
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  width={19.5}
                  height={24}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#1f5459"
                  aria-hidden="true"
                >
                  <path d="M512 96C283.2 96 96 283.2 96 512s187.2 416 416 416 416-187.2 416-416S740.8 96 512 96z m-85.4 637.8L213.4 520.6l59.8-59.8 153.6 153.6 324.2-324.2 59.8 59.8-384.2 383.8z" />
                </svg>
                {t("product.inStock")}
              </p>

              <div className={styles.controlsRow}>
                <div className={styles.quantityControls}>
                  <button
                    className={styles.controlButton}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    aria-label={t("product.decreaseQuantity")}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    min="1"
                    onChange={handleQuantityChange}
                    className={styles.quantityInput}
                    aria-label={t("product.quantity")}
                  />
                  <button
                    className={styles.controlButton}
                    onClick={() => setQuantity(quantity + 1)}
                    aria-label={t("product.increaseQuantity")}
                  >
                    +
                  </button>
                </div>

                <button className={styles.cartBtn} onClick={handleAdd}>
                  {t("product.addToCart")}
                </button>
              </div>
              <div className={`${styles.orderCard} ${styles.orderCardBorder}`}>
                <div className={styles.icon}>
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 42 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M39.008 26.178V20.1279C39.008 18.6386 37.8006 17.4313 36.3114 17.4313H34.7551L32.7483 11.3118H27.6055V19.0084L28.5557 20.5537V27.3257"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.2188 29.558H27.8343"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6.75781 19V26.3183"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M27.6267 11.3118L27.6142 9.30005C27.6103 8.68227 27.1085 8.18344 26.4907 8.18344H7.88139C7.26091 8.18344 6.75781 8.68646 6.75781 9.30702V10"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M33.2203 33.7881C35.1067 33.7881 36.6359 32.2589 36.6359 30.3725C36.6359 28.4861 35.1067 26.9569 33.2203 26.9569C31.3339 26.9569 29.8047 28.4861 29.8047 30.3725C29.8047 32.2589 31.3339 33.7881 33.2203 33.7881Z"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14.7691 33.7881C16.6555 33.7881 18.1847 32.2589 18.1847 30.3725C18.1847 28.4861 16.6555 26.9569 14.7691 26.9569C12.8827 26.9569 11.3535 28.4861 11.3535 30.3725C11.3535 32.2589 12.8827 33.7881 14.7691 33.7881Z"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M38.5824 29.4701H40.3702C40.6719 29.4701 40.9164 29.2255 40.9164 28.9239V26.8644C40.9164 26.5628 40.6719 26.3183 40.3702 26.3183H36.9601M8.90904 29.4701H5.62943C5.29261 29.4701 5.01953 29.2255 5.01953 28.9239V26.8644C5.01953 26.5628 5.29261 26.3183 5.62943 26.3183H10.5151M39.0097 21.0538L35.6802 20.3958C35.1946 20.199 35.1018 19.5515 35.5126 19.2263L35.683 19.0914"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M34.7568 17.4314H29.5801"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.3457 13H10.8516"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 13H1.33863"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.78711 16H8.71009"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className={styles.content}>
                  <div className={styles.contentTitle}>
                    {t("product.features.shipping.title")}
                  </div>
                  <p>{t("product.features.shipping.description")}</p>
                </div>
              </div>

              <div className={`${styles.orderCard} ${styles.orderCardBorder}`}>
                <div className={styles.icon}>
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 42 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M9.95833 9.99857H8.20787C7.99246 9.99858 7.78085 10.0553 7.59431 10.163C7.40777 10.2707 7.25286 10.4256 7.14515 10.6122L6.26988 12.1281C6.16217 12.3147 6.10547 12.5263 6.10547 12.7417C6.10547 12.9571 6.16217 13.1687 6.26988 13.3553L7.14515 14.8712C7.25286 15.0578 7.40777 15.2127 7.59431 15.3204C7.78085 15.4281 7.99246 15.4848 8.20787 15.4848H9.95833C10.1737 15.4848 10.3853 15.4281 10.5719 15.3204C10.7584 15.2127 10.9133 15.0578 11.021 14.8712L11.8963 13.3553C12.004 13.1687 12.0607 12.9571 12.0607 12.7417C12.0607 12.5263 12.004 12.3147 11.8963 12.1281L11.021 10.6122C10.9133 10.4256 10.7584 10.2707 10.5719 10.163C10.3853 10.0553 10.1737 9.99858 9.95833 9.99857ZM16.1654 9.99857H14.4149C14.1995 9.99858 13.9879 10.0553 13.8014 10.163C13.6148 10.2707 13.4599 10.4256 13.3522 10.6122L12.4769 12.1281C12.3692 12.3147 12.3125 12.5263 12.3125 12.7417C12.3125 12.9571 12.3692 13.1687 12.4769 13.3553L13.3522 14.8712C13.4599 15.0578 13.6148 15.2127 13.8014 15.3204C13.9879 15.4281 14.1995 15.4848 14.4149 15.4848H16.1654C16.3808 15.4848 16.5924 15.4281 16.779 15.3204C16.9655 15.2127 17.1204 15.0578 17.2281 14.8712L18.1034 13.3553C18.2111 13.1687 18.2678 12.9571 18.2678 12.7417C18.2678 12.5263 18.2111 12.3147 18.1034 12.1281L17.2281 10.6122C17.1204 10.4256 16.9655 10.2707 16.779 10.163C16.5924 10.0553 16.3808 9.99858 16.1654 9.99857Z"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21.9015 29.5068H5.40079C3.59487 29.5068 2.13086 28.0428 2.13086 26.2369V8.57245C2.13086 6.76653 3.59487 5.30252 5.40079 5.30252H34.4134C36.2193 5.30252 37.6833 6.76653 37.6833 8.57245V18.3769M6.76546 22.948H12.2178M15.8627 22.948H21.0816"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M28.2692 28.2693L31.5 31.5L37.1538 25.0385M32.3203 20.4258L24.4043 21.8614C24.4043 28.752 24.6614 33.3778 32.3203 36.75C39.9792 33.3778 40.2363 28.752 40.2363 21.8614L32.3203 20.4258Z"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className={styles.content}>
                  <div className={styles.contentTitle}>
                    {t("product.features.protection.title")}
                  </div>
                  <p>{t("product.features.protection.description")}</p>
                </div>
              </div>

              <div className={`${styles.orderCard} ${styles.orderCardBorder}`}>
                <div className={styles.icon}>
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 42 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M13.23 10.7027C14.6989 10.7027 15.8897 9.5119 15.8897 8.04298C15.8897 6.57407 14.6989 5.38329 13.23 5.38329C11.7611 5.38329 10.5703 6.57407 10.5703 8.04298C10.5703 9.5119 11.7611 10.7027 13.23 10.7027Z"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M23.6096 12.5497H2.84934C2.11491 12.5497 1.51953 13.1451 1.51953 13.8795C1.51953 14.6139 2.11491 15.2093 2.84934 15.2093H10.0656L8.33132 35.1434C8.26554 35.8991 8.79751 36.5762 9.54736 36.6911C10.3531 36.8145 11.1005 36.2451 11.1952 35.4355L12.3678 25.4157C12.3921 25.2078 12.4908 25.0158 12.6456 24.8749C12.8004 24.7341 13.001 24.654 13.2102 24.6494C13.426 24.6446 13.6359 24.7205 13.7987 24.8623C13.9615 25.004 14.0657 25.2013 14.0907 25.4157L15.2637 35.4356C15.3584 36.2452 16.1058 36.8146 16.9115 36.6911H16.9115C17.6613 36.5762 18.1933 35.8992 18.1276 35.1434L16.3933 15.2093H23.6096C24.344 15.2093 24.9394 14.6139 24.9394 13.8795C24.9395 13.1451 24.3441 12.5497 23.6096 12.5497Z"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M19.4453 6.50161C21.536 6.97141 23.75 6.79947 25.7476 5.98777L28.0516 4.93153L30.5742 6.03814C33.081 7.01849 35.9099 6.99897 38.4008 5.98424L40.6903 4.93153V20.646C40.6903 25.9503 37.8986 30.918 33.2182 33.9418L28.0515 37.1598L22.8849 33.9418C22.1652 33.4773 21.4813 32.9597 20.8387 32.3934"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M36.1077 16.1244L25.5602 26.672L21.6914 22.8033L23.1839 21.3108L25.4329 23.5597L34.4153 14.5773L36.1077 16.1244Z"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className={styles.content}>
                  <div className={styles.contentTitle}>
                    {t("product.features.service.title")}
                  </div>
                  <p>{t("product.features.service.description")}</p>
                </div>
              </div>

              <Link to="/faq" className={styles.orderCard}>
                <div className={styles.icon}>
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 42 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M26.8955 22.557L25.638 22.0353C24.8267 21.699 24.282 20.9263 24.2361 20.0494L24.0039 15.6057"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M24.6261 24.2641L23.3415 24.6308C22.6131 24.8392 22.0774 25.4585 21.9765 26.2091L21.6328 28.1967"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M24.4916 39.5908H14.5757L11.3945 30.6929H21.5746L24.4916 39.5908Z"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M32.8314 29.8513L33.7928 35.8576H30.4033C28.3902 35.8576 26.7578 37.5778 26.7578 39.5917"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M24.4924 39.5908H36.2105C37.4705 39.5908 38.4492 38.4834 38.3032 37.2324L36.4091 25.5372C36.277 24.4044 35.4493 23.4758 34.3386 23.2149L31.9441 22.6752V13.0201C31.9441 11.2449 30.5209 9.83974 28.7465 9.83974H20.9199C20.9199 16.1586 23.5613 15.4958 26.7384 15.4958"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.8459 22.7687V21.6588C13.8459 21.3397 14.1043 21.0821 14.4226 21.0821C15.1707 21.0821 15.7769 20.4759 15.7769 19.7278V18.5736C15.7769 17.8255 15.1707 17.2193 14.4226 17.2193H13.2684C12.5203 17.2193 11.9141 17.8255 11.9141 18.5736V19.2176"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.8457 24.4651V24.6415"
                      stroke="#1F5459"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.93379 14.4023V13.2924C4.93379 12.9733 5.19219 12.7158 5.51047 12.7158C6.25859 12.7158 6.8648 12.1095 6.8648 11.3614V10.2072C6.8648 9.45912 6.25859 8.85291 5.51047 8.85291H4.35629C3.60816 8.85291 3.00195 9.45912 3.00195 10.2072V10.8512"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.93359 16.0987V16.2743"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M37.1682 10.1113V9.00138C37.1682 8.68228 37.4266 8.4247 37.7448 8.4247C38.493 8.4247 39.0992 7.81849 39.0992 7.07036V5.91536C39.0992 5.16724 38.493 4.56103 37.7448 4.56103H36.5907C35.8425 4.56103 35.2363 5.16724 35.2363 5.91536V6.55931"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M37.168 11.8077V11.9832"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.7756 7.70857V6.59869C15.7756 6.27959 16.034 6.02201 16.3523 6.02201C17.1004 6.02201 17.7066 5.4158 17.7066 4.66767V3.51349C17.7066 2.76537 17.1004 2.15916 16.3523 2.15916H15.1981C14.45 2.15916 13.8438 2.76537 13.8438 3.51349V4.15744"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.7754 9.40579V9.58134"
                      stroke="#BBA597"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className={styles.content}>
                  <div className={styles.contentTitle}>
                    {t("product.features.faq.title")}
                  </div>
                  <p>{t("product.features.faq.description")}</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetailCard;
